// Host-based application context. ONE codebase, ONE backend — only the mounted page routes differ.
//   ponesi.rs / www.ponesi.rs -> customer
//   partner.ponesi.rs         -> restaurant panel
//   admin.ponesi.rs           -> platform admin
// SECURITY: this decides only WHICH UI is rendered. It NEVER grants authentication, restaurant
// membership, a role or PLATFORM_ADMIN — every privileged request is still authorized server-side.
export const CUSTOMER_HOSTS = ["ponesi.rs", "www.ponesi.rs"];
export const PARTNER_HOSTS = ["partner.ponesi.rs"];
export const ADMIN_HOSTS = ["admin.ponesi.rs"];

const host = () => (typeof window === "undefined" ? "" : window.location.hostname.toLowerCase());

// An approved Emergent preview/dev hostname — never a production or custom domain.
export function isPreviewHost(h = host()) {
  if (CUSTOMER_HOSTS.includes(h) || PARTNER_HOSTS.includes(h) || ADMIN_HOSTS.includes(h)) return false;
  return h === "localhost" || h === "127.0.0.1"
    || h.endsWith(".preview.emergentagent.com") || h.endsWith(".emergent.host");
}

// Preview-only context switch. Requires BOTH the build flag and an approved preview hostname.
function previewSwitchEnabled() {
  return process.env.REACT_APP_ENABLE_PREVIEW_CONTEXT_SWITCH === "true" && isPreviewHost();
}

const CTX_KEY = "ponesi_preview_ctx";

function previewCtx() {
  if (!previewSwitchEnabled()) return null;
  const q = new URLSearchParams(window.location.search).get("ctx");
  if (["customer", "partner", "admin", "all"].includes(q)) {
    sessionStorage.setItem(CTX_KEY, q);      // survives in-app navigation during a preview test
    return q;
  }
  const saved = sessionStorage.getItem(CTX_KEY);
  return ["customer", "partner", "admin", "all"].includes(saved) ? saved : null;
}

// Deployment pin. A dedicated single-context deployment (e.g. the partner.ponesi.rs app) sets
// REACT_APP_APP_CONTEXT at build time so the app NEVER renders another context — not on its
// *.emergent.host deployment URL, not before the custom domain is linked, and not via ?ctx=.
const PINNED = ["customer", "partner", "admin"].includes(process.env.REACT_APP_APP_CONTEXT)
  ? process.env.REACT_APP_APP_CONTEXT : null;

/** "customer" | "partner" | "admin" | "all" ("all" only on preview with no ctx chosen). */
export function appContext() {
  if (PINNED) return PINNED;
  const h = host();
  if (ADMIN_HOSTS.includes(h)) return "admin";
  if (PARTNER_HOSTS.includes(h)) return "partner";
  if (CUSTOMER_HOSTS.includes(h)) return "customer";
  return previewCtx() || (isPreviewHost(h) ? "all" : "customer"); // unknown prod host => customer only
}

export const CTX = appContext();
export const isCustomerCtx = CTX === "customer" || CTX === "all";
export const isPartnerCtx = CTX === "partner" || CTX === "all";
export const isAdminCtx = CTX === "admin" || CTX === "all";

/** Where each context sends a user after logout — never the customer homepage for panels. */
export const logoutPath = () => (CTX === "admin" ? "/admin" : CTX === "partner" ? "/restaurant" : "/");
